<template>
  <app-page>
    <template v-slot:custom-content>
      <b-container class="pb-4">
        <sme-card large title="Employee Feedback">
          <app-loading v-if="loading" />

          <template v-else>
            <p v-if="quotes.length" class="mb-4 text-muted">All survey responses from your employees.</p>

            <div v-if="quotes.length">
              <b-row>
                <b-col v-for="(quote, index) in quotes" :key="index" md="4" class="mb-3">
                  <div class="quote-card p-3 h-100">
                    <span class="quote-mark">&ldquo;</span>
                    <p class="mb-2 fst-italic">{{ quote.quote }}</p>
                    <small v-if="quote.date" class="text-muted">{{ formatDate(quote.date) }}</small>
                  </div>
                </b-col>
              </b-row>

              <div v-if="hasMore" class="text-center mt-3">
                <b-button variant="outline-primary" size="sm" :disabled="loadingMore" @click="loadMore">
                  <span v-if="loadingMore">Loading...</span>
                  <span v-else>Load more</span>
                </b-button>
              </div>
            </div>

            <p v-else class="my-2 text-center text-muted">No feedback available yet.</p>
          </template>
        </sme-card>
      </b-container>
    </template>
  </app-page>
</template>

<script setup>
import { computed, onBeforeMount, ref } from 'vue';
import ApiClient from '@/ApiClient';
import AppLoading from '@/components/AppLoading.vue';
import AppPage from '@/components/AppPage.vue';
import SmeCard from '@/components/atoms/SmeCard.vue';

const loading = ref(true);
const loadingMore = ref(false);
const quotes = ref([]);
const page = ref(1);
const total = ref(0);

const hasMore = computed(() => quotes.value.length < total.value);

const formatDate = dateStr => {
  if (!dateStr) {
    return '';
  }
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const loadFeedback = async () => {
  loading.value = true;
  try {
    const response = await ApiClient.getPortalQuotes();
    if (response?.data?.length) {
      quotes.value = response.data;
    }
    if (response?.pagination) {
      total.value = response.pagination.total;
    }
  } catch (error) {
    console.error('Failed to fetch feedback:', error);
  }
  loading.value = false;
};

const loadMore = async () => {
  loadingMore.value = true;
  try {
    const nextPage = page.value + 1;
    const response = await ApiClient.getPortalQuotes(nextPage);
    if (response?.data?.length) {
      page.value = nextPage;
      quotes.value = [...quotes.value, ...response.data];
    } else {
      total.value = quotes.value.length;
    }
  } catch (error) {
    console.error('Failed to fetch more feedback:', error);
  }
  loadingMore.value = false;
};

onBeforeMount(() => loadFeedback());
</script>

<style scoped>
.quote-card {
  background: var(--palette-color-base-white);
  border-radius: 8px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
  position: relative;
}

.quote-mark {
  font-size: 2rem;
  line-height: 1;
  color: var(--palette-color-brand-primary);
  font-family: Georgia, serif;
}
</style>
